/**
 * Polymarket contract terms and outcome labels. Every Poly card and table
 * reads them from here, so two surfaces cannot disagree about one market.
 *
 * Users: the position card (#1305), the open-order card, the compact and
 * pinned order items, and the open-orders tables (#1450).
 */

/** Split a Polymarket market title into the game part and the market-variant
 *  descriptor. Poly titles carry the variant after a colon — a totals market
 *  reads "Philadelphia Phillies vs. Detroit Tigers: Total runs 7.5" while the
 *  game's moneyline cards are the bare matchup — and the card title's CSS
 *  truncation eats exactly that suffix, leaving three same-game cards
 *  indistinguishable (Grim, Jul-12). Only split when the head looks like a
 *  matchup ("A vs. B" / "A @ B") so event-prefixed titles ("World Cup: A vs
 *  B") pass through whole. */
export function splitPolyMarketTitle(raw: string): {
  title: string;
  variant: string | null;
} {
  const i = raw.indexOf(': ');
  if (i <= 0) return { title: raw, variant: null };
  const head = raw.slice(0, i);
  if (!/ vs\.? | @ /i.test(head)) return { title: raw, variant: null };
  const variant = raw.slice(i + 2).trim();
  return variant ? { title: head, variant } : { title: raw, variant: null };
}

export interface PolyMarketTerms {
  /** The game: the canonical "Away @ Home" names when the shared index knows
   *  the game, else the venue title's own game part (or the whole title). */
  gameTitle: string;
  /** The contract's exact terms, verbatim from the venue title
   *  ("O/U 51.5", "Spread: Eastern Michigan (-1.5)"); null when the title
   *  names only the game (a moneyline) or when it is kept whole as the
   *  game title. */
  terms: string | null;
}

/**
 * #1305 / #1450 — the game and the contract terms of one Polymarket market.
 *
 * Live frames (he2 CFB and he5 NFL, 2026-11-03) title every spread and total
 * in one of two shapes, with no `subtitle` on any of them:
 *   - `Ohio vs. Akron: O/U 51.5` — the terms follow the matchup;
 *   - `Spread: Eastern Michigan (-1.5)` — a standalone title with no matchup.
 *
 * When the canonical names replace the title, a standalone title would vanish
 * with its terms. So a title with no "vs" or "@" becomes the terms whenever a
 * canonical name is shown in its place. Without canonical names the venue
 * title stays whole as the game title, and its terms stay visible there.
 *
 * @param rawTitle the venue title, or the description's matchup
 * @param named    `getGameMatchup(pmTicker)` — null when the index has no game
 */
export function polyMarketTerms(
  rawTitle: string,
  named: { awayName: string; homeName: string } | null,
): PolyMarketTerms {
  const split = splitPolyMarketTitle(rawTitle);
  const gameTitle = named ? `${named.awayName} @ ${named.homeName}` : split.title;
  const terms = split.variant ?? (
    named && !/\s(?:vs?\.?|@)\s/i.test(split.title) ? split.title : null
  );
  return { gameTitle, terms };
}

/** A Polymarket outcome label fit to show as a team, or null.
 *
 *  The frame fallback in describeOrder writes `'—'` when a frame names no
 *  outcome (spread and total frames carry `yesOutcome: null` by design), and
 *  persisted descriptions keep that placeholder. A literal Yes/No names no
 *  team either. Showing any of them as a team gives a `— YES` badge, a
 *  highlighted `—` in the title and a `(—) YES` subtitle (#1450), so callers
 *  drop the label and show the plain side. */
export function polyOutcomeLabel(label: string | null | undefined): string | null {
  const trimmed = label?.trim();
  if (!trimmed || trimmed === '—' || /^(yes|no)$/i.test(trimmed)) return null;
  return trimmed;
}

/** A Polymarket order's side text: `(OKC) YES` when its outcome names a team,
 *  a plain `NO` when it does not (#1450). The order card's subtitle and the
 *  open-orders tables' badges all read it. */
export function polyBadgeText(betTeam: string | null | undefined, side: 'yes' | 'no'): string {
  const label = polyOutcomeLabel(betTeam);
  return label ? `(${label}) ${side.toUpperCase()}` : side.toUpperCase();
}
